import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { useTheme } from '../context/ThemeContext';
import { usePageTitle } from '../hooks/usePageTitle';
import { useToast, toast } from '../components/UI/Toast';
import Card from '../components/UI/Card';
import Button from '../components/UI/Button';
import { Users, Search, Mail, Phone, Briefcase, Plus, Filter, MoreVertical, Building2, UserMinus, AlertTriangle, X, Activity, Clock, Calendar, ChevronLeft, ChevronRight, RefreshCcw } from 'lucide-react';
import { useTeams, EngagementRole } from '../context/TeamContext';
import { ApiService, ClientListItemDto, ClientStatus } from '../services/api';
import { authService } from '../services/authService';
import { FullPageIframeModal, buildClientProfileUrl } from '../components/UI/FullPageIframeModal';

// Animation keyframes
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const slideInLeft = keyframes`
  from {
    opacity: 0;
    transform: translateX(-20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 2rem;
  animation: ${fadeInUp} 0.6s ease-out;
  
  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const HeaderSection = styled.div`
  margin-bottom: 2.5rem;
`;

const TopRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.5rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }
`;

const TitleGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const IconWrapper = styled.div<{ theme: any }>`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: linear-gradient(135deg, ${props => props.theme.colors.primary} 0%, #973031 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
`;

const TitleText = styled.div``;

const Title = styled.h1<{ theme: any }>`
  color: ${props => props.theme.colors.text};
  font-size: 2.25rem;
  font-weight: 800;
  margin: 0 0 0.25rem 0;
  letter-spacing: -0.02em;
`;

const Subtitle = styled.p<{ theme: any }>`
  color: ${props => props.theme.colors.textSecondary};
  font-size: 1rem;
  margin: 0;
  font-weight: 500;
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  
  @media (max-width: 768px) {
    width: 100%;
    justify-content: flex-start;
  }
`;

const FilterControls = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    gap: 1rem;
  }
`;

const SearchGroup = styled.div`
  position: relative;
  flex: 1;
  max-width: 400px;
  
  @media (max-width: 768px) {
    max-width: none;
  }
`;

const SearchInput = styled.input.withConfig({ 
  shouldForwardProp: (prop) => !['theme'].includes(prop) 
})<{ theme: any }>`
  width: 100%;
  padding: 0.875rem 1rem 0.875rem 2.75rem;
  border-radius: 12px;
  border: 2px solid ${props => props.theme.colors.border};
  background: ${props => props.theme.colors.surface};
  color: ${props => props.theme.colors.text};
  font-size: 0.95rem;
  outline: none;
  transition: all 0.2s ease;
  
  &:focus {
    border-color: ${props => props.theme.colors.primary};
    box-shadow: 0 0 0 3px ${props => props.theme.colors.primary}20;
  }
  
  &::placeholder {
    color: ${props => props.theme.colors.textSecondary};
  }
`;

const SearchIcon = styled(Search).withConfig({
  shouldForwardProp: (prop) => !['theme'].includes(prop)
})<{ theme: any }>`
  position: absolute;
  left: 0.875rem;
  top: 50%;
  transform: translateY(-50%);
  color: ${props => props.theme.colors.textSecondary};
`;

const StatsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;
`;

const StatCard = styled(Card)`
  padding: 1.5rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  animation: ${slideInLeft} 0.6s ease-out;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
  }
`;

const StatIcon = styled.div<{ theme: any; color: string }>`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: ${props => props.color}20;
  color: ${props => props.color};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StatContent = styled.div`
  flex: 1;
`;

const StatValue = styled.div<{ theme: any }>`
  font-size: 1.75rem;
  font-weight: 800;
  color: ${props => props.theme.colors.text};
  margin-bottom: 0.25rem;
`;

const StatLabel = styled.div<{ theme: any }>`
  font-size: 0.875rem;
  color: ${props => props.theme.colors.textSecondary};
  font-weight: 600;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(380px, 1fr));
  gap: 2rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

const ClientCard = styled.div.withConfig({
  shouldForwardProp: (prop) => !['theme'].includes(prop),
})<{ theme: any }>`
  background: ${props => props.theme.colors.surface};
  border-radius: 20px;
  padding: 0;
  overflow: hidden;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  border: 1px solid ${props => props.theme.colors.border};
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, ${props => props.theme.colors.primary}, #973031);
    opacity: 0;
    transition: opacity 0.3s ease;
  }
  
  &:hover {
    transform: translateY(-8px) scale(1.02);
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
    border-color: ${props => props.theme.colors.primary}60;
    
    &::before {
      opacity: 1;
    }
  }
`;

const CardHeader = styled.div<{ theme: any }>`
  padding: 2rem 2rem 0rem;
  background: linear-gradient(135deg, ${props => props.theme.colors.surface} 0%, ${props => props.theme.colors.background}80 100%);
  position: relative;
  overflow: hidden;
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    width: 100px;
    height: 100px;
    background: ${props => props.theme.colors.primary}08;
    border-radius: 50%;
    transform: translate(30px, -30px);
  }
`;

const CardBody = styled.div`
  padding: 0 2rem 2rem;
`;

const ClientHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 1.5rem;
  position: relative;
  z-index: 2;
`;

const ClientInfo = styled.div`
  flex: 1;
`;

const ClientAvatar = styled.div<{ theme: any }>`
  width: 56px;
  height: 56px;
  border-radius: 16px;
  background: linear-gradient(135deg, ${props => props.theme.colors.primary}, #973031);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 1rem;
  box-shadow: 0 8px 24px ${props => props.theme.colors.primary}30;
`;

const ClientName = styled.h3<{ theme: any }>`
  color: ${props => props.theme.colors.text};
  font-size: 1.375rem;
  font-weight: 800;
  margin: 0 0 0.5rem 0;
  line-height: 1.2;
  letter-spacing: -0.01em;
`;

const CompanyName = styled.p<{ theme: any }>`
  color: ${props => props.theme.colors.textSecondary};
  font-size: 0.95rem;
  font-weight: 500;
  margin: 0 0 1rem 0;
  opacity: 0.8;
`;

const ClientBadge = styled.span<{ theme: any; status: ClientStatus }>`
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.5rem 1rem;
  background: ${props => {
    if (props.status === 'ACTIVE') return '#10b98115';
    if (props.status === 'INACTIVE') return '#f59e0b15';
    if (props.status === 'ARCHIVED') return '#64748b20';
    return props.theme.colors.primary + '15';
  }};
  color: ${props => {
    if (props.status === 'ACTIVE') return '#10b981';
    if (props.status === 'INACTIVE') return '#f59e0b';
    if (props.status === 'ARCHIVED') return '#64748b';
    return props.theme.colors.primary;
  }};
  border-radius: 25px;
  font-size: 0.8rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;


const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const StatItem = styled.div<{ theme: any }>`
  padding: 1rem;
  background: ${props => props.theme.colors.background}60;
  border-radius: 12px;
  border: 1px solid ${props => props.theme.colors.border}30;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${props => props.theme.colors.background}80;
    border-color: ${props => props.theme.colors.primary}40;
    transform: translateY(-2px);
  }
`;

const ClientStatValue = styled.div<{ theme: any }>`
  font-size: 1.125rem;
  font-weight: 700;
  color: ${props => props.theme.colors.text};
  margin-bottom: 0.25rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const ClientStatLabel = styled.div<{ theme: any }>`
  font-size: 0.8rem;
  color: ${props => props.theme.colors.textSecondary};
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const ContactInfo = styled.div`
  margin-bottom: 1.5rem;
`;

const ContactItem = styled.div<{ theme: any }>`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 0;
  font-size: 0.9rem;
  color: ${props => props.theme.colors.textSecondary};
  border-bottom: 1px solid ${props => props.theme.colors.border}20;
  
  &:last-child {
    border-bottom: none;
  }
  
  &:hover {
    color: ${props => props.theme.colors.text};
  }
`;

const ContactIcon = styled.div<{ color: string }>`
  width: 20px;
  height: 20px;
  color: ${props => props.color};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const TeamChips = styled.div<{ theme: any }>`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin: 0.5rem 0 1rem 0;
`;

const TeamChip = styled.span<{ theme: any }>`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.25rem 0.6rem;
  border-radius: 999px;
  border: 1px solid ${p => p.theme.colors.border};
  background: ${p => p.theme.colors.background};
  color: ${p => p.theme.colors.text};
  font-size: 0.75rem;
  font-weight: 700;
`;

const ActionButtons = styled.div.withConfig({
  shouldForwardProp: (prop) => !['theme'].includes(prop)
})<{ theme?: any }>`
  display: flex;
  gap: 0.75rem;
  align-items: center;
  padding-top: 1rem;
  border-top: 1px solid ${props => props.theme?.colors?.border || '#e5e7eb'}20;
`;

const OptionsContainer = styled.div`
  position: relative;
`;

const OptionsButton = styled.button.withConfig({
  shouldForwardProp: (prop) => !['theme'].includes(prop)
})<{ theme: any }>`
  width: 32px;
  height: 32px;
  border-radius: 6px;
  border: 1px solid ${props => props.theme.colors.border};
  background: ${props => props.theme.colors.surface};
  color: ${props => props.theme.colors.textSecondary};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${props => props.theme.colors.background};
    color: ${props => props.theme.colors.text};
    border-color: ${props => props.theme.colors.primary};
  }
`;

const OptionsMenu = styled.div.withConfig({
  shouldForwardProp: (prop) => !['theme', 'isOpen'].includes(prop)
})<{ theme: any; isOpen: boolean }>`
  position: absolute;
  top: 100%;
  right: 0;
  background: ${props => props.theme.colors.surface};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  min-width: 160px;
  padding: 0.5rem 0;
  opacity: ${props => props.isOpen ? 1 : 0};
  visibility: ${props => props.isOpen ? 'visible' : 'hidden'};
  transform: translateY(${props => props.isOpen ? '0' : '-10px'});
  transition: all 0.2s ease-out;
  z-index: 120;
`;

const OptionsMenuItem = styled.button.withConfig({
  shouldForwardProp: (prop) => !['theme', 'danger'].includes(prop)
})<{ theme: any; danger?: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
  padding: 0.75rem 1rem;
  border: none;
  background: transparent;
  color: ${props => props.danger ? '#dc2626' : props.theme.colors.text};
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: left;
  
  &:hover {
    background: ${props => props.danger ? '#dc262610' : props.theme.colors.background};
  }
`;

// Modal Components
const ModalOverlay = styled.div.withConfig({
  shouldForwardProp: (prop) => !['theme', 'isOpen'].includes(prop)
})<{ theme: any; isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  opacity: ${props => props.isOpen ? 1 : 0};
  visibility: ${props => props.isOpen ? 'visible' : 'hidden'};
  transition: all 0.3s ease;
`;

const ModalContent = styled.div.withConfig({
  shouldForwardProp: (prop) => !['theme', 'isOpen'].includes(prop)
})<{ theme: any; isOpen: boolean }>`
  background: ${props => props.theme.colors.surface};
  border-radius: 16px;
  padding: 0;
  width: 90%;
  max-width: 500px;
  max-height: 90vh;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
  transform: ${props => props.isOpen ? 'scale(1) translateY(0)' : 'scale(0.9) translateY(20px)'};
  transition: all 0.3s ease;
`;

const ModalHeader = styled.div<{ theme: any }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 2rem 2rem 1rem 2rem;
  border-bottom: 1px solid ${props => props.theme.colors.border};
`;

const ModalTitle = styled.h2<{ theme: any }>`
  color: ${props => props.theme.colors.text};
  font-size: 1.2rem;
  font-weight: 700;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const CloseButton = styled.button.withConfig({
  shouldForwardProp: (prop) => !['theme'].includes(prop)
})<{ theme: any }>`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  border: none;
  background: ${props => props.theme.colors.background};
  color: ${props => props.theme.colors.textSecondary};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${props => props.theme.colors.border};
    color: ${props => props.theme.colors.text};
  }
`;

const ModalBody = styled.div`
  padding: 2rem;
`;

const ModalActions = styled.div.withConfig({
  shouldForwardProp: (prop) => !['theme'].includes(prop)
})<{ theme: any }>`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  padding: 1.5rem 2rem 2rem 2rem;
  border-top: 1px solid ${props => props.theme.colors.border};
`;

const PaginationBar = styled.div<{ theme: any }>`
  margin-top: 2rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem 1.25rem;
  background: ${p => p.theme.colors.surface};
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: 14px;
`;

const PageInfo = styled.div<{ theme: any }>`
  color: ${p => p.theme.colors.textSecondary};
  font-weight: 700;
  font-size: 0.9rem;
`;

const Pager = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const ClientsPage: React.FC = () => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const {
    teams,
    clientTeamAssignments,
    assignTeamToClient,
  } = useTeams();
  usePageTitle('Clients');
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'ALL' | ClientStatus>('ALL');
  const [page, setPage] = useState(1);
  const [pageSize] = useState(12);
  const [clients, setClients] = useState<ClientListItemDto[]>([]);
  const [pagination, setPagination] = useState<{ total: number; page: number; pageSize: number; totalPages: number; hasNext: boolean; hasPrev: boolean } | null>(null);
  const [backendStats, setBackendStats] = useState<{ totalClients: number; totalActiveClients: number; uniqueIndustries: number } | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filingsOpen, setFilingsOpen] = useState(false);
  const [filingsUrl, setFilingsUrl] = useState<string>('');
  const [filingsClientName, setFilingsClientName] = useState<string>('');
  const [filingsLoadingClientId, setFilingsLoadingClientId] = useState<string | null>(null);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [disengageModal, setDisengageModal] = useState<{
    isOpen: boolean;
    clientId: string | null;
    clientName: string;
  }>({
    isOpen: false,
    clientId: null,
    clientName: ''
  });

  const [assignTeamModal, setAssignTeamModal] = useState<{
    isOpen: boolean;
    clientId: string | null;
    clientName: string;
    teamId: string;
    role: EngagementRole;
  }>({ isOpen: false, clientId: null, clientName: '', teamId: '', role: 'CONTRIBUTOR' });

  // Removed client-level member addition per requirements

  const formatCreatedDate = (iso: string) => {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }).format(d);
  };

  const loadClients = async (opts?: { silent?: boolean }) => {
    const token = authService.getAccessToken();
    if (!token) return;
    if (!opts?.silent) setIsLoading(true);
    setError(null);
    try {
      const resp = await ApiService.listClients(token, {
        q: query.trim() || undefined,
        status: statusFilter === 'ALL' ? undefined : statusFilter,
        page,
        pageSize,
      });
      setClients(resp.data || []);
      setPagination(resp.pagination);
      setBackendStats(resp.stats);
    } catch (e: any) {
      setError(e?.message || 'Failed to load clients');
    } finally {
      if (!opts?.silent) setIsLoading(false);
    }
  };

  useEffect(() => {
    void loadClients();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, statusFilter]);

  // Refresh when query changes (reset to page 1)
  useEffect(() => {
    const t = setTimeout(() => {
      setPage(1);
      void loadClients({ silent: true });
    }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  const handleOptionsClick = (clientId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    setOpenMenuId(openMenuId === clientId ? null : clientId);
  };

  const handleDisengageClick = (clientId: string, clientName: string) => {
    setDisengageModal({
      isOpen: true,
      clientId,
      clientName
    });
    setOpenMenuId(null);
  };

  const openAssignTeam = (clientId: string, clientName: string) => {
    setAssignTeamModal({ isOpen: true, clientId, clientName, teamId: teams[0]?.id || '', role: 'CONTRIBUTOR' });
    setOpenMenuId(null);
  };

  const confirmAssignTeam = () => {
    if (!assignTeamModal.clientId || !assignTeamModal.teamId) return;
    (async () => {
      try {
        await assignTeamToClient(assignTeamModal.clientId!, assignTeamModal.teamId!, assignTeamModal.role);
        setAssignTeamModal({ isOpen: false, clientId: null, clientName: '', teamId: '', role: 'CONTRIBUTOR' });
        showToast(toast.success('Team Assigned', 'Team successfully assigned to client'));
      } catch (e: any) {
        showToast(toast.error('Assignment failed', e?.message || 'Could not assign team to client'));
      }
    })();
  };

  const openManageFilings = async (client: ClientListItemDto) => {
    const base = process.env.REACT_APP_CLIENT_PROFILE_ENDPOINT;
    if (!base) {
      showToast(toast.error('Missing configuration', 'Client profile endpoint is not configured.'));
      return;
    }

    const token = authService.getAccessToken();
    if (!token) {
      showToast(toast.error('Not authenticated', 'Please log in again and try.'));
      return;
    }

    setFilingsLoadingClientId(client.id);
    try {
      const clientToken = await ApiService.getClientToken(token, client.id);
      const url = buildClientProfileUrl({ baseUrl: base, clientToken });
      setFilingsClientName(client.companyName || client.email || client.tin || 'Client');
      setFilingsUrl(url);
      setFilingsOpen(true);
    } catch (e: any) {
      showToast(toast.error('Could not open filings', e?.message || 'Please try again.'));
    } finally {
      setFilingsLoadingClientId(null);
    }
  };

  // No client-level member addition

  const confirmDisengage = () => {
    if (!disengageModal.clientId) return;
    
    // In a real app, this would make an API call
    console.log('Disengaging client:', disengageModal.clientId);
    
    setDisengageModal({
      isOpen: false,
      clientId: null,
      clientName: ''
    });
    
    // Show success message
    showToast(toast.success('Client Disengaged', `Successfully disengaged from ${disengageModal.clientName}`));
  };

  const cancelDisengage = () => {
    setDisengageModal({
      isOpen: false,
      clientId: null,
      clientName: ''
    });
  };

  // Close options menu when clicking outside
  React.useEffect(() => {
    const handleClickOutside = () => setOpenMenuId(null);
    if (openMenuId) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [openMenuId]);

  const computedStats = useMemo(() => {
    return {
      total: backendStats?.totalClients ?? pagination?.total ?? clients.length,
      active: backendStats?.totalActiveClients ?? clients.filter(c => c.status === 'ACTIVE').length,
      industries: backendStats?.uniqueIndustries ?? new Set(clients.map(c => c.industry).filter(Boolean)).size,
    };
  }, [backendStats, clients, pagination?.total]);


  return (
    <Container>
      <HeaderSection>
        <TopRow>
          <TitleGroup>
            <IconWrapper theme={theme}>
              <Users size={24} />
            </IconWrapper>
            <TitleText>
              <Title theme={theme}>Client Management</Title>
              <Subtitle theme={theme}>Manage your tax consulting clients and their information</Subtitle>
            </TitleText>
          </TitleGroup>
          <Actions>
            <Button>
              <Plus size={16} style={{ marginRight: '0.5rem' }} />
              Add Client
            </Button>
            <Button variant="outline" onClick={() => loadClients()} disabled={isLoading}>
              <RefreshCcw size={16} style={{ marginRight: '0.5rem' }} />
              Refresh
            </Button>
            <Button variant="outline">
              <Filter size={16} />
            </Button>
          </Actions>
        </TopRow>

        <StatsRow>
          <StatCard>
            <StatIcon theme={theme} color={theme.colors.primary}>
              <Users size={20} />
            </StatIcon>
            <StatContent>
              <StatValue theme={theme}>{computedStats.total}</StatValue>
              <StatLabel theme={theme}>Total Clients</StatLabel>
            </StatContent>
          </StatCard>
          
          <StatCard>
            <StatIcon theme={theme} color={theme.colors.success || '#16a34a'}>
              <Building2 size={20} />
            </StatIcon>
            <StatContent>
              <StatValue theme={theme}>{computedStats.active}</StatValue>
              <StatLabel theme={theme}>Active Clients</StatLabel>
            </StatContent>
          </StatCard>
          
          <StatCard>
            <StatIcon theme={theme} color="#0ea5e9">
              <Activity size={20} />
            </StatIcon>
            <StatContent>
              <StatValue theme={theme}>{pagination?.total ?? clients.length}</StatValue>
              <StatLabel theme={theme}>Filtered Results</StatLabel>
            </StatContent>
          </StatCard>
          
          <StatCard>
            <StatIcon theme={theme} color="#8b5cf6">
              <Briefcase size={20} />
            </StatIcon>
            <StatContent>
              <StatValue theme={theme}>{computedStats.industries}</StatValue>
              <StatLabel theme={theme}>Industries</StatLabel>
            </StatContent>
          </StatCard>
        </StatsRow>

        <FilterControls>
          <SearchGroup>
            <SearchIcon size={18} theme={theme} />
            <SearchInput
              theme={theme}
              placeholder="Search clients by email, phone, company, Tax ID or CAC..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </SearchGroup>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            {(['ALL', 'ACTIVE', 'INACTIVE', 'ARCHIVED'] as const).map((s) => (
              <Button
                key={s}
                variant={statusFilter === s ? undefined : 'outline'}
                size="small"
                onClick={() => {
                  setStatusFilter(s);
                  setPage(1);
                }}
              >
                {s === 'ALL' ? 'All' : s.charAt(0) + s.slice(1).toLowerCase()}
              </Button>
            ))}
          </div>
        </FilterControls>
      </HeaderSection>

      {error && (
        <Card>
          <div style={{ padding: '1rem 1.25rem' }}>
            <div style={{ fontWeight: 900, color: theme.colors.text, marginBottom: '0.25rem' }}>Could not load clients</div>
            <div style={{ fontWeight: 600, color: theme.colors.textSecondary }}>{error}</div>
          </div>
        </Card>
      )}

      {isLoading && (
        <Card>
          <div style={{ padding: '1rem 1.25rem', color: theme.colors.textSecondary, fontWeight: 700 }}>
            Loading clients...
          </div>
        </Card>
      )}

      <Grid>
        {clients.map((client) => (
          <ClientCard key={client.id} theme={theme}>
            <CardHeader theme={theme}>
              <ClientHeader>
                <ClientInfo>
                  <ClientAvatar theme={theme}>
                    {(client.companyName || client.email || client.tin || 'C')
                      .split(' ')
                      .map(n => n[0])
                      .join('')
                      .toUpperCase()
                      .slice(0, 2)}
                  </ClientAvatar>
                  <ClientName theme={theme}>{client.companyName || client.email || client.tin || 'Client'}</ClientName>
                  <CompanyName theme={theme}>{client.industry ? `${client.industry} Industry` : '—'}</CompanyName>
                  <ClientBadge theme={theme} status={client.status}>
                    <Activity size={12} />
                    {client.status}
                  </ClientBadge>
                </ClientInfo>
                <OptionsContainer>
                  <OptionsButton 
                    theme={theme}
                    onClick={(e) => handleOptionsClick(client.id, e)}
                  >
                    <MoreVertical size={16} />
                  </OptionsButton>
                  <OptionsMenu theme={theme} isOpen={openMenuId === client.id}>
                    <OptionsMenuItem 
                      theme={theme}
                      onClick={() => openAssignTeam(client.id, client.companyName || client.email || client.tin || 'Client')}
                    >
                      Assign Team
                    </OptionsMenuItem>
                    
                    <OptionsMenuItem 
                      theme={theme}
                      onClick={() => handleDisengageClick(client.id, client.companyName || client.email || client.tin || 'Client')}
                      danger
                    >
                      <UserMinus size={16} />
                      Disengage Client
                    </OptionsMenuItem>
                  </OptionsMenu>
                </OptionsContainer>
              </ClientHeader>
            </CardHeader>

            <CardBody>
              {/* Recent engagements */}
              <div style={{ marginBottom: '0.75rem' }}>
                <div style={{ fontWeight: 800, fontSize: '0.9rem', marginBottom: '0.35rem', color: theme.colors.text }}>Recent Engagements</div>
                <div style={{ color: theme.colors.textSecondary, fontWeight: 600, fontSize: '0.85rem' }}>
                  {client.engagements?.length
                    ? client.engagements.map(e => `${e.title} (${e.status})`).join(' · ')
                    : 'No engagements yet'}
                </div>
              </div>

              {/* Teams on engagement */}
              <div style={{ marginBottom: '0.75rem' }}>
                <div style={{ fontWeight: 800, fontSize: '0.9rem', marginBottom: '0.35rem', color: theme.colors.text }}>Teams on Engagement</div>
                <TeamChips theme={theme}>
                  {clientTeamAssignments
                    .filter(a => a.clientId === client.id)
                    .map(a => {
                      const t = teams.find(x => x.id === a.teamId);
                      return (
                        <TeamChip key={a.id} theme={theme}>
                          {t?.name || a.teamId} · {a.role}
                        </TeamChip>
                      );
                    })}
                  {clientTeamAssignments.filter(a => a.clientId === client.id).length === 0 && (
                    <span style={{ color: theme.colors.textSecondary, fontSize: '0.85rem' }}>No team assigned</span>
                  )}
                </TeamChips>
              </div>

              <StatsGrid>
                <StatItem theme={theme}>
                  <ClientStatValue theme={theme}>
                    <Calendar size={16} color="#0ea5e9" />
                    {formatCreatedDate(client.createdAt)}
                  </ClientStatValue>
                  <ClientStatLabel theme={theme}>Created</ClientStatLabel>
                </StatItem>
                <StatItem theme={theme}>
                  <ClientStatValue theme={theme}>
                    <Clock size={16} color="#f59e0b" />
                    {client.status}
                  </ClientStatValue>
                  <ClientStatLabel theme={theme}>Status</ClientStatLabel>
                </StatItem>
              </StatsGrid>

              <ContactInfo>
                <ContactItem theme={theme}>
                  <ContactIcon color="#0ea5e9">
                    <Mail size={16} />
                  </ContactIcon>
                  {client.email || '—'}
                </ContactItem>
                <ContactItem theme={theme}>
                  <ContactIcon color="#16a34a">
                    <Phone size={16} />
                  </ContactIcon>
                  {client.phone || '—'}
                </ContactItem>
                <ContactItem theme={theme}>
                  <ContactIcon color="#8b5cf6">
                    <Briefcase size={16} />
                  </ContactIcon>
                  {client.tin || '—'}
                </ContactItem>
                <ContactItem theme={theme}>
                  <ContactIcon color="#f59e0b">
                    <Calendar size={16} />
                  </ContactIcon>
                  CAC: {client.cacNumber || '—'}
                </ContactItem>
                <ContactItem theme={theme}>
                  <ContactIcon color="#0ea5e9">
                    <Calendar size={16} />
                  </ContactIcon>
                  Accounting Year: {client.accountingYear || '—'}
                </ContactItem>
              </ContactInfo>

              <ActionButtons theme={theme}>
                <Button 
                  variant="outline" 
                  size="small"
                  onClick={() => navigate(`/clients/${client.id}`)}
                >
                  View Profile
                </Button>
                {client.status === 'ACTIVE' && (
                  <Button
                    size="small"
                    onClick={() => openManageFilings(client)}
                    disabled={filingsLoadingClientId === client.id}
                  >
                    {filingsLoadingClientId === client.id ? 'Opening…' : 'Manage Filings'}
                  </Button>
                )}
              </ActionButtons>
            </CardBody>
          </ClientCard>
        ))}
      </Grid>

      <FullPageIframeModal
        isOpen={filingsOpen}
        title="Manage Filings"
        contextTitle={filingsClientName || undefined}
        url={filingsUrl}
        theme={theme}
        onClose={() => {
          setFilingsOpen(false);
          setFilingsUrl('');
          setFilingsClientName('');
        }}
      />

      {pagination && (
        <PaginationBar theme={theme}>
          <PageInfo theme={theme}>
            Page {pagination.page} of {pagination.totalPages} · {pagination.total} clients
          </PageInfo>
          <Pager>
            <Button
              variant="outline"
              size="small"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={!pagination.hasPrev || isLoading}
            >
              <ChevronLeft size={16} />
              Prev
            </Button>
            <Button
              variant="outline"
              size="small"
              onClick={() => setPage(p => Math.min(pagination.totalPages, p + 1))}
              disabled={!pagination.hasNext || isLoading}
            >
              Next
              <ChevronRight size={16} />
            </Button>
          </Pager>
        </PaginationBar>
      )}

      {/* Disengage Confirmation Modal */}
      <ModalOverlay theme={theme} isOpen={disengageModal.isOpen} onClick={cancelDisengage}>
        <ModalContent 
          theme={theme} 
          isOpen={disengageModal.isOpen} 
          onClick={(e) => e.stopPropagation()}
        >
          <ModalHeader theme={theme}>
            <ModalTitle theme={theme}>
              <AlertTriangle size={24} />
              Confirm Client Disengagement
            </ModalTitle>
            <CloseButton theme={theme} onClick={cancelDisengage}>
              <X size={20} />
            </CloseButton>
          </ModalHeader>

          <ModalBody>
            <div style={{ textAlign: 'center', padding: '1rem 0' }}>
              <p style={{ 
                color: theme.colors.text, 
                fontSize: '1.1rem', 
                marginBottom: '1rem',
                fontWeight: '500'
              }}>
                Are you sure you want to disengage from this client?
              </p>
              <div style={{
                background: theme.colors.background,
                border: `1px solid ${theme.colors.border}`,
                borderRadius: '8px',
                padding: '1rem',
                marginBottom: '1.5rem'
              }}>
                <strong style={{ color: theme.colors.text }}>
                  {disengageModal.clientName}
                </strong>
              </div>
              <div style={{
                background: '#fef2f2',
                border: '1px solid #fecaca',
                borderRadius: '8px',
                padding: '1rem',
                marginBottom: '1rem'
              }}>
                <p style={{ 
                  color: '#dc2626', 
                  fontSize: '0.95rem',
                  margin: 0,
                  lineHeight: '1.5',
                  fontWeight: '500'
                }}>
                  ⚠️ Warning: This action cannot be undone
                </p>
              </div>
              <p style={{ 
                color: theme.colors.textSecondary, 
                fontSize: '0.95rem',
                margin: 0,
                lineHeight: '1.5'
              }}>
                Disengaging will remove this client from your active client list and end your professional relationship. 
                All ongoing tax matters should be completed or transferred before proceeding.
              </p>
            </div>
          </ModalBody>

          <ModalActions theme={theme}>
            <Button variant="outline" onClick={cancelDisengage}>
              Cancel
            </Button>
            <button
              onClick={confirmDisengage}
              style={{
                background: '#dc2626',
                borderColor: '#dc2626',
                color: 'white',
                border: '1px solid #dc2626',
                borderRadius: '8px',
                padding: '0.75rem 1.5rem',
                fontSize: '0.875rem',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                transition: 'all 0.2s ease'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.background = '#b91c1c';
                e.currentTarget.style.borderColor = '#b91c1c';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.background = '#dc2626';
                e.currentTarget.style.borderColor = '#dc2626';
              }}
            >
              <UserMinus size={16} />
              Disengage Client
            </button>
          </ModalActions>
        </ModalContent>
      </ModalOverlay>

      {/* Assign Team Modal */}
      <ModalOverlay theme={theme} isOpen={assignTeamModal.isOpen} onClick={() => setAssignTeamModal({ isOpen: false, clientId: null, clientName: '', teamId: '', role: 'CONTRIBUTOR' })}>
        <ModalContent theme={theme} isOpen={assignTeamModal.isOpen} onClick={(e) => e.stopPropagation()}>
          <ModalHeader theme={theme}>
            <ModalTitle theme={theme}>Assign Team to {assignTeamModal.clientName}</ModalTitle>
            <CloseButton theme={theme} onClick={() => setAssignTeamModal({ isOpen: false, clientId: null, clientName: '', teamId: '', role: 'CONTRIBUTOR' })}>
              <X size={20} />
            </CloseButton>
          </ModalHeader>
          <ModalBody>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              <label style={{ color: theme.colors.textSecondary, fontWeight: 700, fontSize: '0.85rem' }}>Team</label>
              <select
                value={assignTeamModal.teamId}
                onChange={(e) => setAssignTeamModal(v => ({ ...v, teamId: e.target.value }))}
                style={{ padding: '0.6rem', borderRadius: 8, border: `1px solid ${theme.colors.border}`, background: theme.colors.background, color: theme.colors.text }}
              >
                {teams.map(t => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>

              <label style={{ color: theme.colors.textSecondary, fontWeight: 700, fontSize: '0.85rem' }}>Engagement Role</label>
              <select
                value={assignTeamModal.role}
                onChange={(e) => setAssignTeamModal(v => ({ ...v, role: e.target.value as EngagementRole }))}
                style={{ padding: '0.6rem', borderRadius: 8, border: `1px solid ${theme.colors.border}`, background: theme.colors.background, color: theme.colors.text }}
              >
                <option value="CONTRIBUTOR">Contributor</option>
                <option value="VIEWER">Viewer</option>
              </select>
            </div>
          </ModalBody>
          <ModalActions theme={theme}>
            <Button variant="outline" onClick={() => setAssignTeamModal({ isOpen: false, clientId: null, clientName: '', teamId: '', role: 'CONTRIBUTOR' })}>Cancel</Button>
            <Button onClick={confirmAssignTeam}>Assign</Button>
          </ModalActions>
        </ModalContent>
      </ModalOverlay>

      
    </Container>
  );
};

export default ClientsPage;


